import { useState, useEffect } from "react";
import { Plus, Search, Edit2, Trash2, FileText, ToggleLeft, ToggleRight, Layout } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function Templates() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Modal & Editor states
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState({
        templateName: "",
        documentType: "experience_letter",
        content: "",
        isActive: true
    });

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const r = await api.get("/documents/templates");
            setTemplates(r.data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const handleOpenAdd = () => {
        setIsEdit(false);
        setForm({
            templateName: "",
            documentType: "experience_letter",
            content: "",
            isActive: true
        });
        setShowModal(true);
    };

    const handleOpenEdit = (tpl) => {
        setIsEdit(true);
        setSelectedId(tpl.id);
        setForm({
            templateName: tpl.templateName || "",
            documentType: tpl.documentType || "experience_letter",
            content: tpl.content || "",
            isActive: tpl.isActive !== false
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEdit) {
                await api.put(`/documents/templates/${selectedId}`, form);
                toast.success("Template updated successfully");
            } else {
                await api.post("/documents/templates", form);
                toast.success("Template created successfully");
            }
            setShowModal(false);
            fetchTemplates();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const handleDelete = async (id) => {
        if (!confirm("Are you sure you want to delete this template?")) return;
        try {
            await api.delete(`/documents/templates/${id}`);
            toast.success("Template deleted successfully");
            fetchTemplates();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const handleToggleActive = async (tpl) => {
        try {
            await api.put(`/documents/templates/${tpl.id}`, { isActive: !tpl.isActive });
            toast.success(`Template ${!tpl.isActive ? "activated" : "deactivated"}`);
            fetchTemplates();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-6 p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center pb-4 border-b border-slate-200 dark:border-white/10">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-slate-800 dark:text-slate-100">Document Templates</h1>
                    <p className="text-sm text-muted-foreground">Manage templates for letters, certificates, and NDA agreements.</p>
                </div>
                <button
                    onClick={handleOpenAdd}
                    className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white px-4 py-2 rounded-xl transition shadow-md font-semibold text-sm"
                >
                    <Plus className="w-4 h-4" />
                    <span>Create Template</span>
                </button>
            </div>

            {/* List */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : templates.length === 0 ? (
                <div className="glass py-16 text-center rounded-2xl border border-slate-200 dark:border-white/10">
                    <p className="text-muted-foreground text-sm">No templates configured yet. Click "Create Template" to add one.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map((tpl) => (
                        <div key={tpl.id} className="glass p-6 rounded-2xl border border-slate-200 dark:border-white/10 flex flex-col justify-between shadow-sm relative group hover:shadow-md transition duration-300">
                            <div>
                                <div className="flex justify-between items-start gap-2">
                                    <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center mb-4">
                                        <FileText className="w-4 h-4" />
                                    </div>
                                    <button
                                        onClick={() => handleToggleActive(tpl)}
                                        className="text-slate-400 hover:text-primary transition"
                                    >
                                        {tpl.isActive ? <ToggleRight className="w-6 h-6 text-primary" /> : <ToggleLeft className="w-6 h-6 text-slate-300 dark:text-slate-600" />}
                                    </button>
                                </div>
                                <h3 className="font-heading font-semibold text-slate-800 dark:text-slate-100 text-base line-clamp-1">{tpl.templateName}</h3>
                                <span className="inline-flex px-2 py-0.5 mt-1.5 rounded-md text-[10px] font-semibold bg-primary/5 text-primary border border-primary/10 uppercase tracking-wider">
                                    {tpl.documentType?.replace("_", " ")}
                                </span>
                                <p className="text-xs text-muted-foreground mt-4 line-clamp-4 leading-relaxed font-mono bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border border-slate-100 dark:border-white/5">
                                    {tpl.content}
                                </p>
                            </div>

                            <div className="flex justify-end gap-2 border-t border-slate-200 dark:border-white/10 pt-4 mt-6">
                                <button
                                    onClick={() => handleOpenEdit(tpl)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition font-medium"
                                >
                                    <Edit2 className="w-3.5 h-3.5" />
                                    <span>Edit</span>
                                </button>
                                <button
                                    onClick={() => handleDelete(tpl.id)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/5 rounded-lg transition font-medium"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    <span>Delete</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal Form */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl overflow-hidden border border-slate-200 dark:border-white/10 shadow-2xl animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]">
                        <div className="px-6 py-4 border-b border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800/50 flex justify-between items-center">
                            <h2 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">
                                {isEdit ? "Edit Document Template" : "Create Document Template"}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-muted-foreground hover:text-foreground text-sm font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Template Name</label>
                                <input
                                    type="text"
                                    required
                                    value={form.templateName}
                                    onChange={(e) => setForm({ ...form, templateName: e.target.value })}
                                    className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    placeholder="e.g. Standard Experience Letter"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Document Type</label>
                                    <select
                                        value={form.documentType}
                                        onChange={(e) => setForm({ ...form, documentType: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    >
                                        <option value="certificate">Certificate of Internship</option>
                                        <option value="experience_letter">Experience Letter</option>
                                        <option value="recommendation_letter">Recommendation Letter (LOR)</option>
                                        <option value="offer_letter">Offer Letter</option>
                                        <option value="nda">NDA Document</option>
                                    </select>
                                </div>
                                <div className="flex items-end pl-2 pb-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={form.isActive}
                                            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                            className="w-4 h-4 rounded text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">Set Active</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400">Template Content</label>
                                    <span className="text-[10px] text-muted-foreground bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded font-mono">Use paragraphs. Double Enter for new paragraph.</span>
                                </div>
                                <textarea
                                    required
                                    rows={10}
                                    value={form.content}
                                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                                    className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono text-foreground leading-relaxed"
                                    placeholder="Standard content text goes here..."
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/10">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-xl text-sm font-semibold shadow-md transition"
                                >
                                    {isEdit ? "Save Changes" : "Create Template"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
